import { useCallback } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { FormInput } from '../input/FormInput';
import { cn } from '@/lib/utils';

export type FormValuesProps = {
  values?: (string | undefined)[];
  placeholder?: string;
  addButtonText?: string;
  onChange?: React.FormEventHandler<HTMLSpanElement>;
  onBlur?: React.FocusEventHandler<HTMLSpanElement>;
  name: string;
  disabled?: boolean;
  'data-testid'?: string;
  maxHeight?: number;
};

export const FormValues = ({
  values = [''],
  placeholder = 'Enter a Value',
  addButtonText = 'Add',
  onChange,
  onBlur,
  name,
  disabled = false,
  'data-testid': dataTestId,
  maxHeight,
}: FormValuesProps) => {
  const reset = useCallback(() => {
    onChange?.({
      target: {
        name,
        value: [''],
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  }, [onChange, name]);

  const { isSubmitting } = useFormikContext();

  return (
    <FieldArray name={name} data-testid={dataTestId}>
      {({ remove, push }) => {
        return (
          <>
            <div
              className={cn(maxHeight && 'overflow-y-auto custom-scrollbar')}
              style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}
            >
              {values.map((value, index) => (
                <div key={index}>
                  <div className="flex flex-row gap-2">
                    <FormInput
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      className="flex-1"
                      placeholder={placeholder}
                      name={`${name}[${index}]`}
                      disabled={isSubmitting || disabled}
                      data-testid={`${dataTestId}-input-${index}`}
                    />
                    <Button
                      onClick={() =>
                        values.length === 1 ? reset() : remove(index)
                      }
                      iconOnly
                      leftIcon="antimatterhq:trash"
                      aria-label="Remove"
                      type="button"
                      className="h-[40px]"
                      disabled={isSubmitting}
                      tabIndex={-1}
                      data-testid={`${dataTestId}-remove-button-${index}`}
                    />
                  </div>
                  <Spacer size="base" />
                </div>
              ))}
            </div>
            <Spacer size="base" />
            <Button
              leftIcon="radix-icons:plus"
              variant="default"
              onClick={() => push('')}
              className="mt-2"
              type="button"
              disabled={isSubmitting || disabled}
              data-testid={`${dataTestId}-add-button`}
            >
              {addButtonText}
            </Button>
          </>
        );
      }}
    </FieldArray>
  );
};
